/**
 * Safari-compatible date utilities
 *
 * Safari has stricter date parsing requirements than Chrome/Firefox
 */

const getLocale = () => (typeof window !== 'undefined' && window.locale) || 'en';

/**
 * Parse a timestamp safely for all browsers including Safari
 * @param {string|number|Date} timestamp 
 * @returns {Date}
 */
export const parseDate = (timestamp) => {
    if (!timestamp) return new Date();

    // If already a Date object, return it
    if (timestamp instanceof Date) return timestamp;

    // Convert to string and handle various formats
    let dateString = String(timestamp);

    // Safari doesn't like dates with just spaces, so we need to handle various formats
    // Common problematic formats and their fixes:

    // Fix: "2021-04-14 17:45:52 UTC" -> "2021-04-14T17:45:52Z"
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2} UTC$/.test(dateString)) {
        dateString = dateString.replace(' ', 'T').replace(' UTC', 'Z');
    }

    // Fix: "2023-08-14 12:21:00" -> "2023-08-14T12:21:00"
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
        dateString = dateString.replace(' ', 'T');
    }

    // Fix: "2023-08-14 12:21:00.000" -> "2023-08-14T12:21:00.000"
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{3}$/.test(dateString)) {
        dateString = dateString.replace(' ', 'T');
    }

    // Fix: Add Z suffix for UTC if missing timezone info
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?$/.test(dateString)) {
        dateString = dateString + 'Z';
    }

    // Try to parse the date
    let date = new Date(dateString);

    // If invalid, try alternative parsing methods
    if (isNaN(date.getTime())) {
        // Try parsing as timestamp number
        const numericTimestamp = Number(timestamp);
        if (!isNaN(numericTimestamp)) {
            // Check if it's in seconds (Unix timestamp) or milliseconds
            if (numericTimestamp < 10000000000) {
                // Unix timestamp in seconds, convert to milliseconds
                return new Date(numericTimestamp * 1000);
            } else {
                // Already in milliseconds
                return new Date(numericTimestamp);
            }
        }

        // Try parsing without timezone info
        if (typeof timestamp === 'string') {
            // Remove timezone info and try again
            const cleanedString = timestamp.replace(/[+-]\d{2}:?\d{2}$/, '').replace('Z', '');
            date = new Date(cleanedString);
            if (!isNaN(date.getTime())) {
                return date;
            }
        }

        // If all else fails, return current date to prevent crashes
        console.warn('Failed to parse timestamp:', timestamp, 'using current date');
        return new Date();
    }

    return date;
};

/**
 * Format message time in a Safari-compatible way
 * @param {string|number|Date} timestamp 
 * @returns {string}
 */
export const formatMessageTime = (timestamp) => {
    const date = parseDate(timestamp);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = diffInMs / (1000 * 60);
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    // Handle invalid dates
    if (isNaN(diffInMs) || isNaN(date.getTime())) {
        console.warn('formatMessageTime: Invalid date:', timestamp);
        return 'Invalid date';
    }

    try {
        const locale = getLocale();

        // Less than 1 minute ago
        if (diffInMinutes < 1) {
            return locale === 'fr' ? 'maintenant' : 'now';
        }

        // Less than 1 hour ago - show minutes
        if (diffInMinutes < 60) {
            return `${Math.floor(diffInMinutes)}m`;
        }

        // Less than 24 hours ago - show time
        if (diffInHours < 24) {
            return date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
        }

        // Less than 7 days ago - show day of week
        if (diffInDays < 7) {
            return date.toLocaleDateString(locale, { weekday: 'short' });
        }

        // Less than 1 year ago - show month and day
        if (diffInDays < 365) {
            return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
        }

        // More than 1 year ago - show month, day, year
        return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: '2-digit' });

    } catch (error) {
        console.warn('Error formatting date:', timestamp, error);
        // Fallback to basic formatting
        try {
            // Simple fallback format
            const hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            return `${displayHours}:${minutes} ${ampm}`;
        } catch (fallbackError) {
            return 'Invalid date';
        }
    }
};

/**
 * Safely compare two dates for sorting
 * @param {string|number|Date} dateA 
 * @param {string|number|Date} dateB 
 * @returns {number}
 */
export const compareDates = (dateA, dateB) => {
    const parsedA = parseDate(dateA);
    const parsedB = parseDate(dateB);

    // Handle invalid dates by putting them at the end
    if (isNaN(parsedA.getTime()) && isNaN(parsedB.getTime())) return 0;
    if (isNaN(parsedA.getTime())) return 1;
    if (isNaN(parsedB.getTime())) return -1;

    return parsedA.getTime() - parsedB.getTime();
};

/**
 * Calculate time difference between messages safely
 * @param {string|number|Date} timestamp1
 * @param {string|number|Date} timestamp2
 * @returns {number} Difference in milliseconds
 */
export const getTimeDifference = (timestamp1, timestamp2) => {
    const date1 = parseDate(timestamp1);
    const date2 = parseDate(timestamp2);

    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
        return 0; // Return 0 for invalid dates to prevent grouping issues
    }

    return Math.abs(date1.getTime() - date2.getTime());
};

/**
 * Format timestamp for AI chat messages
 * - Today: show only time (e.g., "10:46 AM")
 * - Not today: show only date (e.g., "Aug 13" or "Aug 13, 2023")
 * @param {string|number|Date} timestamp
 * @returns {string}
 */
export const formatAIChatTime = (timestamp) => {
    const date = parseDate(timestamp);
    const now = new Date();

    // Check if date is valid
    if (isNaN(date.getTime())) {
        console.warn('formatAIChatTime: Invalid date:', timestamp);
        return 'Invalid date';
    }

    try {
        // Check if same day (today)
        const isToday = date.getFullYear() === now.getFullYear() &&
                        date.getMonth() === now.getMonth() &&
                        date.getDate() === now.getDate();

        const locale = getLocale();

        // If today, only show time
        if (isToday) {
            return date.toLocaleTimeString(locale, {
                hour: 'numeric',
                minute: '2-digit'
            });
        }

        // If same year, show just date without year
        const isThisYear = date.getFullYear() === now.getFullYear();

        if (isThisYear) {
            return date.toLocaleDateString(locale, {
                month: 'short',
                day: 'numeric'
            });
        }

        // Different year, show date with year
        return date.toLocaleDateString(locale, {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });

    } catch (error) {
        console.warn('Error formatting AI chat time:', timestamp, error);
        // Fallback to basic formatting
        try {
            const hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            return `${displayHours}:${minutes} ${ampm}`;
        } catch (fallbackError) {
            return 'Invalid date';
        }
    }
};